import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { SortableHeader } from './header.js';
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
} from '@dnd-kit/modifiers';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { List } from 'react-bootstrap-icons';
import { bake, clearStorage } from './common';
import { Hint } from './constants.js';
import { isDate, shortDate } from './date';
import { useEffect, useState } from 'react';
import { MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import Spinner from 'react-bootstrap/Spinner';

export default function PageTable(props) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [currentColumn, setCurrentColumn] = useState(null);
  const archivalFieldOrderStart = -998;
  const archivalFieldOrderEnd = -999;

  const resetColumns = () => {
    clearStorage(props, 'fields');
  };

  useEffect(() => {
    setCurrentColumn(null);
  }, []);

  const handleDragStart = (event) => {
    // disable dragging if noHeaderContext is provided from parent component
    if (!props.noHeaderContext) {
      setDragging(true);
      setCurrentColumn(event.active.id);
    }
  };

  const handleDragEnd = (event) => {
    // disable dragging if noHeaderContext is provided from parent component
    const { active, over } = event;
    if (!props.noHeaderContext) {
      if (active.id !== over.id && over.id !== 'Menu') {
        let cols = props.columns;
        let startIndex = cols.findIndex((x) => x.id === active.id);
        let endIndex = cols.findIndex((x) => x.id === over.id);
        let item = cols.splice(startIndex, 1);
        cols.splice(endIndex, 0, item[0]);
        cols = cols.map((col, index) => ({ ...col, sortOrder: index + 1 }));
        props.setSortedColumns(cols);
        bake({
          props: props,
          key: 'fields',
          value: cols.filter((x) => x.id !== 'Menu'),
        });
      }
    }
    setDragging(false);
    setCurrentColumn(null);
  };

  const handleDragCancel = () => {
    setCurrentColumn(null);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 15,
      },
    })
  );

  const headerSortClick = (col) => {
    let localOrder = bake({ props: props, key: 'order' });
    let localOrderBy = bake({ props: props, key: 'orderBy' });
    if (col.id === localOrderBy) {
      localOrder = localOrder === 'asc' ? 'desc' : 'asc';
      bake({ props: props, key: 'order', value: localOrder });
      setOrder(localOrder === 'asc' ? 'desc' : 'asc');
    } else {
      localOrderBy = col.id;
      localOrder = 'asc';
      bake({ props: props, key: 'orderBy', value: localOrderBy });
      bake({ props: props, key: 'order', value: localOrder });
      setOrder(localOrder);
      setOrderBy(localOrderBy);
    }

    props.updateSearchParams(
      {
        orderBy: localOrderBy,
        order: localOrder,
      },
      true
    );
  };

  const closeColumnFilter = (selected, column) => {
    let filters = bake({ props: props, key: 'filters' });
    filters = filters ? filters.filter((x) => x.key !== column.id) : [];
    let colFilter = [];
    selected.forEach((s) => {
      colFilter.push({
        key: column.id,
        field: column.id,
        label: s.EntityMetaValue,
        value: s.EntityMetaValue,
        DBID: s.DBID ?? column.value,
      });
    });

    let newFilters = filters.concat(colFilter);
    props.setCurrentModalRef(null);
    // if no filter changes, do nothing
    if (
      JSON.stringify(bake({ props: props, key: 'filters' })) ===
      JSON.stringify(newFilters)
    ) {
      return;
    }
    bake({ props: props, key: 'filters', value: newFilters });
    props.updateSearchParams({ filters: newFilters });
  };

  return (
    <>
      <TableContainer
        id={props.filteredRows?.length > 0 ? 'tab-container' : 'tableContainer'}
        component={Paper}
        className={
          dragging || !props.filteredRows?.length
            ? 'tableContainer no-scroll'
            : 'tableContainer'
        }
      >
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
          padding="none"
        >
          {/* Column Headers */}
          <TableHead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              modifiers={[
                restrictToHorizontalAxis,
                restrictToFirstScrollableAncestor,
              ]}
            >
              <SortableContext items={props.columns} strategy={() => {}}>
                <TableRow className="header-div">
                  {props.columns
                    //Archival Fields should always be displayed last and should not display in grouped view
                    //The order of columns is defined by the field "value" in groupColumns field and the order
                    //of default columns, including arhival fields, is fetched from the stored procedure.
                    .filter((column) => {
                      return !props.archiveMode
                        ? column.value > archivalFieldOrderStart ||
                            column.value === null
                        : column.value >= archivalFieldOrderEnd ||
                            column.value === null;
                    })
                    .map((column, i) => {
                      return (
                        <SortableHeader
                          key={column.id}
                          column={column}
                          columns={props.columns}
                          order={bake({ props: props, key: 'order' }) ?? order}
                          orderBy={
                            bake({ props: props, key: 'orderBy' }) ??
                            props.columns[0].id ??
                            orderBy
                          }
                          index={i}
                          groupedColumns={props.groupedColumns}
                          filters={bake({ props: props, key: 'filters' }) ?? []}
                          headerContextAnchor={props.headerContextAnchor}
                          sort={(column) => headerSortClick(column)}
                          level={props.level}
                          updateColumns={props.updateColumns}
                          updateGroups={props.updateGroups}
                          updateSearchParams={props.updateSearchParams}
                          closeColumnFilter={(selected, column) =>
                            closeColumnFilter(selected, column)
                          }
                          resetColumns={resetColumns}
                          currentModal={props.currentModal}
                          grouped={props.grouped}
                          type={props.type}
                          noHeaderContext={props.noHeaderContext}
                        />
                      );
                    })}
                </TableRow>
              </SortableContext>
              <DragOverlay>
                {dragging ? (
                  <SortableHeader
                    column={props.columns.find((x) => x.id === currentColumn)}
                    columns={props.columns}
                    index={props.columns.findIndex(
                      (x) => x.id === currentColumn.id
                    )}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </TableHead>
          {/* Table Content */}
          {props.filteredRows?.length > 0 ? (
            <TableBody>
              {/* Loading Spinner */}
              {props.loading ? (
                <div className="d-flex justify-content-center align-items-center loader-container">
                  <Spinner animation="border" variant="dark" />
                </div>
              ) : null}

              {props.filteredRows?.map((row, i) => {
                return (
                  <TableRow
                    onClick={(e) => props.handleClick(e, row)}
                    size="small"
                    role="checkbox"
                    tabIndex={-1}
                    key={i}
                    onMouseEnter={() => props.setRowHover(row)}
                    onMouseLeave={() => props.clearRowHover()}
                  >
                    {props.columns
                      .filter((column) => {
                        return !props.archiveMode
                          ? column.value > archivalFieldOrderStart ||
                              column.value === null
                          : column.value >= archivalFieldOrderEnd ||
                              column.value === null;
                      })
                      .map((column, i) => {
                        const value = row[column.id ?? column.label];
                        let tableCellClass =
                          i === props.columns.length - 1
                            ? 'sticky-last-col'
                            : '';
                        tableCellClass += ' search-table-col';
                        tableCellClass += props.isHovered(row)
                          ? ' search-row-hover'
                          : '';
                        tableCellClass += props.isSelected(row)
                          ? ' search-row-selected'
                          : '';
                        return (
                          <TableCell
                            size="small"
                            key={i}
                            align={column.align}
                            className={tableCellClass}
                          >
                            {/* Actions Menu*/}
                            {column.id === 'Menu' ? (
                              <List
                                size={24}
                                className="pointer row-menu-icon"
                                onClick={(e) =>
                                  props.handleContextClick(e, row)
                                }
                              />
                            ) : null}

                            {/* Split array values with nice boxes  */}
                            {Array.isArray(value) ? (
                              value.length > 1 ? (
                                value.map((v, i) => {
                                  return (
                                    <span key={i} className="search-meta-value">
                                      {isDate(v) ? shortDate(v) : '' + v}
                                    </span>
                                  );
                                })
                              ) : isDate(value[0]) ? (
                                '' + shortDate(value[0])
                              ) : value[0]?.length > 40 ? (
                                <Hint
                                  placement="top"
                                  delay={{ show: 250, hide: 400 }}
                                  title={value[0]}
                                >
                                  <span>
                                    {value[0]?.substring(0, 40).concat('...')}
                                  </span>
                                </Hint>
                              ) : (
                                value
                              )
                            ) : column.format && typeof value === 'number' ? (
                              column.format(value)
                            ) : isDate(value) ? (
                              '' + shortDate(value)
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : !props.loading && !(props.filteredRows?.length > 0) ? (
            <TableRow>
              <TableCell colSpan={props.columns.length}>
                No data found for the filter
              </TableCell>
            </TableRow>
          ) : props.loading ? (
            <div className="d-flex justify-content-center align-items-center loader-container">
              <Spinner animation="border" variant="dark" />
            </div>
          ) : null}
        </Table>
      </TableContainer>
    </>
  );
}
